/**
 * BookingErrorBoundary - Error boundary for the booking flow.
 * Catches React errors and displays a user-friendly message with recovery options.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/lib/base/buttons/button';

interface BookingErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode;
  /** Callback to navigate back to the start of the booking flow */
  onReset?: () => void;
  /** Fallback heading. Defaults to a generic booking-flow message. */
  title?: string;
  /** Fallback body copy. Defaults to a generic booking-flow message. */
  message?: string;
}

interface BookingErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * BookingErrorBoundary catches rendering errors in the booking flow
 * and displays a friendly error message with retry and reset options.
 */
export class BookingErrorBoundary extends Component<
  BookingErrorBoundaryProps,
  BookingErrorBoundaryState
> {
  constructor(props: BookingErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<BookingErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console (always log for booking errors)
    console.error('BookingErrorBoundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Store error info in state for potential display
    this.setState({ errorInfo });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoBack = (): void => {
    const { onReset } = this.props;

    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call onReset callback if provided
    if (onReset) {
      onReset();
    }
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const {
      children,
      onReset,
      title = 'Something went wrong',
      message = 'We encountered an unexpected error while loading the booking page. Please try again or start over.',
    } = this.props;

    if (hasError && error) {
      return (
        <div className='flex flex-col items-center justify-center py-12 px-6 text-center'>
          {/* Error icon */}
          <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6'>
            <svg
              className='w-8 h-8 text-red-600'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              aria-hidden='true'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>

          {/* Error message */}
          <h2 className='text-xl font-semibold text-fg-primary mb-2'>
            {title}
          </h2>
          <p className='text-fg-secondary max-w-md mb-6'>{message}</p>

          {/* Action buttons */}
          <div className='flex flex-col sm:flex-row gap-3'>
            <Button color='primary' onClick={this.handleRetry} className='px-6'>
              Try again
            </Button>
            {onReset && (
              <Button
                color='secondary'
                onClick={this.handleGoBack}
                className='px-6'
              >
                Start over
              </Button>
            )}
          </div>

          {/* Development-only error details */}
          {process.env.NODE_ENV === 'development' && (
            <div className='mt-8 w-full max-w-2xl text-left'>
              <details className='bg-bg-secondary border border-border-secondary rounded-lg overflow-hidden'>
                <summary className='px-4 py-3 cursor-pointer text-sm font-medium text-fg-secondary hover:bg-bg-tertiary'>
                  Technical details
                </summary>
                <div className='px-4 py-3 border-t border-border-secondary'>
                  <div className='font-mono text-xs space-y-3'>
                    <div>
                      <span className='font-semibold text-fg-primary'>
                        Error:{' '}
                      </span>
                      <span className='text-red-600'>{error.message}</span>
                    </div>
                    {error.stack && (
                      <div>
                        <span className='font-semibold text-fg-primary'>
                          Stack:{' '}
                        </span>
                        <pre className='mt-1 whitespace-pre-wrap text-fg-secondary overflow-auto max-h-40'>
                          {error.stack}
                        </pre>
                      </div>
                    )}
                    {errorInfo?.componentStack && (
                      <div>
                        <span className='font-semibold text-fg-primary'>
                          Component Stack:{' '}
                        </span>
                        <pre className='mt-1 whitespace-pre-wrap text-fg-secondary overflow-auto max-h-40'>
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </details>
            </div>
          )}
        </div>
      );
    }

    return children;
  }
}
